import { useSuspenseQuery } from '@apollo/client/react';
import { GET_ARBEITSZEIT_BY_ID } from './graphql/queries';
import {
  Abteilung,
  Abwesenheit,
  Antrag,
  Arbeitsbereich,
  Arbeitszeit,
  Dienstgang,
  Mitarbeiter,
  Pause,
  Puenktlichkeit,
  Rechtshinweis,
  Zuschlag,
  Audit,
  ArbeitszeitAuditAenderung,
  ArbeitszeitAuditManager,
  Zeitstempel,
  Schicht,
  mapAbwesenheitsgrund,
  toArbeitszeitenPausenart,
  toBackgroundColor,
} from '../entities';
import {
  ArbeitszeitenPauseneinstellung,
  Arbeitszeiten,
  Audit as AuditGql,
  AuditResult,
  GetArbeitszeitByIdQuery,
  GetArbeitszeitByIdQueryVariables,
  Markierung,
} from '../../../gql/graphql';
import { getDateOrNull } from '../../../utils/helperFunctions';
import { Termin, toTerminWiederholung } from '../entities/Termin';
import { toArbeitszeitAuditAenderungsTyp } from '../entities/ArbeitszeitAuditAenderungsTyp';
import dayjs from 'dayjs';

function getDateOrUndefined(value?: string | null): Date | undefined {
  return getDateOrNull(value) ?? undefined;
}

function calculatePausendauer(arbeitszeit: Arbeitszeiten) {
  let pausendauer = 0;
  for (let i = 0; i < arbeitszeit.pausen.length; i++) {
    if (arbeitszeit.pausen[i].dauer !== null) {
      pausendauer += arbeitszeit.pausen[i].dauer ?? 0;
    }
  }
  return pausendauer;
}

function calculatePuenktlichkeit(arbeitszeit: Arbeitszeiten) {
  let puenktlichkeit = new Puenktlichkeit();

  if (
    arbeitszeit.puenktlichkeit !== null &&
    arbeitszeit.puenktlichkeit !== undefined
  ) {
    puenktlichkeit = new Puenktlichkeit(
      arbeitszeit.puenktlichkeit.abweichungBeginn ?? undefined,
      arbeitszeit.puenktlichkeit.abweichungEnde ?? undefined,
    );
  }
  return puenktlichkeit;
}

function mapDienstgaenge(arbeitszeit: Arbeitszeiten): Dienstgang[] {
  return arbeitszeit.dienstgaenge.map((dienstgang) => {
    return {
      id: dienstgang.id ?? undefined,
      beginn: getDateOrUndefined(dienstgang.beginn),
      ende: getDateOrUndefined(dienstgang.ende),
      dauer: dienstgang.dauer ?? undefined,
    };
  });
}

function mapPausen(arbeitszeit: Arbeitszeiten): Pause[] {
  return arbeitszeit.pausen.map((pause) => {
    return {
      id: pause.id ?? undefined,
      beginn: getDateOrUndefined(pause.beginn),
      ende: getDateOrUndefined(pause.ende),
      dauer: pause.dauer ?? undefined,
      art: toArbeitszeitenPausenart(pause.art),
      bezahlt: pause.bezahlt,
    };
  });
}

function mapRechtshinweise(arbeitszeit: Arbeitszeiten): Rechtshinweis[] {
  return arbeitszeit.rechtshinweise.map((rechtshinweis) => {
    return {
      name: rechtshinweis.name,
      art: rechtshinweis.art,
      backgroundcolor: toBackgroundColor(rechtshinweis.backgroundcolor),
    };
  });
}

function mapAntrage(arbeitszeit: Arbeitszeiten): Antrag[] {
  return (arbeitszeit.antraege ?? []).map((antrag) => {
    return {
      id: antrag.id ?? '',
      beginn: {
        time: getDateOrUndefined(antrag.beginn),
        isChangeRequested: false,
      },
      ende: { time: getDateOrUndefined(antrag.ende), isChangeRequested: false },
      aenderungsdatum: getDateOrUndefined(antrag.aenderungsdatum),
      arbeitszeitId: antrag.arbeitszeitId ?? '',
      bemerkungAntragsteller: antrag.bemerkungAntragsteller ?? '',
      bemerkungVorgesetzter: antrag.bemerkungVorgesetzter ?? '',
      datum: getDateOrUndefined(antrag.datum),
      erstellungsdatum: getDateOrUndefined(antrag.erstellungsdatum),
      offen: antrag.offen ?? false,
      status: antrag.status ?? undefined,
      mitarbeiter: new Mitarbeiter(
        antrag.mitarbeiter?.vorname ?? '',
        antrag.mitarbeiter?.nachname ?? '',
        antrag.mitarbeiter?.id ?? '',
        false,
        antrag.mitarbeiter?.bildUrl ?? undefined,
      ),
      arbeitsbereich: new Arbeitsbereich(
        antrag.arbeitsbereich.name,
        antrag.arbeitsbereich.id,
        antrag.arbeitsbereich.markierung as unknown as Markierung,
        [],
      ),
    };
  });
}

function isAuditGql(audit: AuditResult | null | undefined): audit is AuditGql {
  return !!audit && audit.__typename === 'Audit';
}

function mapAudit(arbeitszeit: Arbeitszeiten): Audit[] {
  return (arbeitszeit.audit ?? []).filter(isAuditGql).map((audit) => {
    return new Audit(
      audit.id ?? '',
      audit.typ ?? '',
      toArbeitszeitAuditAenderungsTyp(audit.aenderungsTyp),
      audit.aenderungsdatum ? new Date(audit.aenderungsdatum) : new Date(),
      audit.geaendertVon ?? '',
      audit.geaendertVonEmail ?? '',
      audit.manager
        ? new ArbeitszeitAuditManager(
            audit.manager.email ?? '',
            audit.manager.vorname ?? '',
            audit.manager.nachname ?? '',
          )
        : undefined,
      (audit.aenderungen ?? []).map(
        (aenderung) =>
          new ArbeitszeitAuditAenderung(
            aenderung.attribut ?? '',
            aenderung.nachher ?? '',
            aenderung.vorher ?? '',
          ),
      ),
    );
  });
}

export function useArbeitszeitById(arbeitszeitId: string): {
  arbeitszeit: Arbeitszeit | undefined;
  error?: string;
} {
  const { data, error } = useSuspenseQuery<
    GetArbeitszeitByIdQuery,
    GetArbeitszeitByIdQueryVariables
  >(GET_ARBEITSZEIT_BY_ID, {
    variables: {
      arbeitszeitId: arbeitszeitId,
    },
    fetchPolicy: 'no-cache',
    refetchWritePolicy: 'overwrite',
  });

  if (error) {
    return {
      error: error.message,
      arbeitszeit: undefined,
    };
  }

  return {
    arbeitszeit: createArbeitszeit(data.arbeitszeitById as Arbeitszeiten),
    error: undefined,
  };
}

// eslint-disable-next-line max-lines-per-function, complexity
function createArbeitszeit(arbeitszeit: Arbeitszeiten) {
  const pausendauer = calculatePausendauer(arbeitszeit);
  const puenktlichkeit = calculatePuenktlichkeit(arbeitszeit);
  const dienstgaenge: Dienstgang[] = mapDienstgaenge(arbeitszeit);
  const bemerkung = arbeitszeit.bemerkung ? arbeitszeit.bemerkung : '';
  const pausen: Pause[] = mapPausen(arbeitszeit);
  const zuschlaege: Zuschlag[] = [];
  const antraege = mapAntrage(arbeitszeit);
  const rechtshinweise: Rechtshinweis[] = mapRechtshinweise(arbeitszeit);
  const audits: Audit[] = arbeitszeit.audit ? mapAudit(arbeitszeit) : [];
  let abwesenheit: Abwesenheit | undefined;

  if (arbeitszeit.abwesenheit) {
    abwesenheit = new Abwesenheit(
      arbeitszeit.abwesenheit.id,
      mapAbwesenheitsgrund(arbeitszeit.abwesenheit.grund),
      arbeitszeit.abwesenheit.dauer,
    );
  }
  if (arbeitszeit.zuschlaege !== null) {
    const tmp = arbeitszeit.zuschlaege.map((zuschlag) => {
      if (zuschlag.betrag.__typename === 'arbeitszeiten_zuschlaege_betrag') {
        return new Zuschlag(
          zuschlag.dauer,
          zuschlag.name,
          zuschlag.prozent,
          zuschlag.betrag.betrag,
        );
      }
      return new Zuschlag(zuschlag.dauer, zuschlag.name, zuschlag.prozent);
    });
    zuschlaege.push(...tmp);
  }

  let termin: Termin | undefined;

  if (arbeitszeit.termin) {
    termin = new Termin(
      arbeitszeit.termin.titel ?? undefined,
      toTerminWiederholung(arbeitszeit.termin.wiederholung),
      arbeitszeit.termin.id ?? undefined,
      arbeitszeit.termin.bildUrl ?? undefined,
      arbeitszeit.termin.bildName ?? undefined,
      arbeitszeit.termin.beschreibung ?? undefined,
      getDateOrUndefined(arbeitszeit.termin.beginn),
      getDateOrUndefined(arbeitszeit.termin.ende),
      arbeitszeit.termin.markierung ?? undefined,
    );
  }
  const az = new Arbeitszeit(
    arbeitszeit.id ?? '',
    new Mitarbeiter(
      arbeitszeit.mitarbeiter.vorname,
      arbeitszeit.mitarbeiter.nachname,
      arbeitszeit.mitarbeiter.id ?? '',
      arbeitszeit.mitarbeiter.extern,
      arbeitszeit.mitarbeiter.bildUrl ?? undefined,
    ),
    new Arbeitsbereich(
      arbeitszeit.arbeitsbereich.name,
      arbeitszeit.arbeitsbereich.id ?? '',
      arbeitszeit.arbeitsbereich.markierung,
      (arbeitszeit.arbeitsbereich.abteilungen ?? []).map(
        (abteilung) =>
          new Abteilung(abteilung.name, abteilung.id, abteilung.markierung),
      ),
    ),
    new Abteilung(
      arbeitszeit.arbeitsbereich.name,
      arbeitszeit.arbeitsbereich.id ?? '',
      arbeitszeit.arbeitsbereich.markierung,
    ),
    arbeitszeit.dauer ?? 0,
    pausendauer,
    puenktlichkeit,
    pausen,
    arbeitszeit.pauseneinstellung ?? ArbeitszeitenPauseneinstellung.Automatisch,
    dienstgaenge,
    [],
    rechtshinweise,
    '',
    getDateOrNull(arbeitszeit.beginn) ?? undefined,
    getDateOrNull(arbeitszeit.ende) ?? undefined,
    getDateOrNull(arbeitszeit.kommen) ?? undefined,
    getDateOrNull(arbeitszeit.gehen) ?? undefined,
    abwesenheit,
    bemerkung,
    zuschlaege,
    antraege,
    audits,
    termin,
    arbeitszeit.geaendertVon ?? undefined,
    getDateOrUndefined(arbeitszeit.aenderungsdatum),
    getDateOrNull(arbeitszeit.datum) ?? undefined,
  );
  if (arbeitszeit.beginn2) {
    az.beginn2 = new Zeitstempel({
      abweichung: arbeitszeit.beginn2.abweichung ?? undefined,
      zeitpunkt: getDateOrUndefined(arbeitszeit.beginn2.zeitpunkt),
      schicht: arbeitszeit.beginn2.schicht
        ? new Schicht({
            id: arbeitszeit.beginn2.schicht.id,
            beginn: dayjs(arbeitszeit.beginn2.schicht.beginn),
            ende: dayjs(arbeitszeit.beginn2.schicht.ende),
          })
        : undefined,
    });
  }
  if (arbeitszeit.ende2) {
    az.ende2 = new Zeitstempel({
      abweichung: arbeitszeit.ende2.abweichung ?? undefined,
      zeitpunkt: getDateOrUndefined(arbeitszeit.ende2.zeitpunkt),
      schicht: arbeitszeit.ende2.schicht
        ? new Schicht({
            id: arbeitszeit.ende2.schicht.id,
            beginn: dayjs(arbeitszeit.ende2.schicht.beginn),
            ende: dayjs(arbeitszeit.ende2.schicht.ende),
          })
        : undefined,
    });
  }

  if (arbeitszeit.kommen2) {
    az.kommen2 = new Zeitstempel({
      abweichung: arbeitszeit.kommen2.abweichung ?? undefined,
      zeitpunkt: getDateOrUndefined(arbeitszeit.kommen2.zeitpunkt),
    });
  }

  if (arbeitszeit.gehen2) {
    az.gehen2 = new Zeitstempel({
      abweichung: arbeitszeit.gehen2.abweichung ?? undefined,
      zeitpunkt: getDateOrUndefined(arbeitszeit.gehen2.zeitpunkt),
    });
  }

  return az;
}
